import type { Room } from '@studio/models/room';
import type { StudioPickerDescriptor } from '../types.js';
import { organizationKey } from './org-palette.js';
import { sortStudios } from './sort.js';

export interface StudioGroup {
  readonly key: string;
  readonly organization: StudioPickerDescriptor['organization'];
  readonly studios: StudioPickerDescriptor[];
}

/** Group before sorting so each organization keeps an independent row order. */
export const groupStudios = (descriptors: readonly StudioPickerDescriptor[]): StudioGroup[] => {
  const groups = new Map<string, StudioGroup>();

  for (const descriptor of descriptors) {
    const key = organizationKey(descriptor.organization, descriptor.room.organizationId);
    const group = groups.get(key);
    if (group) {
      group.studios.push(descriptor);
    } else {
      groups.set(key, {
        key,
        organization: descriptor.organization,
        studios: [descriptor]
      });
    }
  }

  return [...groups.values()].map((group) => ({
    ...group,
    studios: sortStudios(group.studios)
  }));
};

// Qualifiers never show organization names or ids, or room ids — a card must
// not reveal another account's organization. Same-named Studios are numbered
// in creation order instead: oldest first, undated last; the room id only keeps
// exact ties stable and is never shown. createdAt 0 is the bootstrap
// placeholder's value until the replica record loads, not a creation time, so
// it counts as undated: a just-created Studio is numbered last from the start
// instead of jumping from #1 to last when its record arrives.
const creationOrder = (a: StudioPickerDescriptor, b: StudioPickerDescriptor): number => {
  const aCreated = a.room.createdAt || Number.POSITIVE_INFINITY;
  const bCreated = b.room.createdAt || Number.POSITIVE_INFINITY;
  if (aCreated !== bCreated) {
    return aCreated < bCreated ? -1 : 1;
  }
  return String(a.room.id).localeCompare(String(b.room.id));
};

/** Visible duplicate-name qualifiers ("#1", "#2", ...) in deterministic creation order. */
export const duplicateDisambiguators = (
  descriptors: readonly StudioPickerDescriptor[]
): ReadonlyMap<Room['id'], string> => {
  const byName = new Map<string, StudioPickerDescriptor[]>();

  for (const descriptor of descriptors) {
    const name = descriptor.room.name.trim().toLocaleLowerCase();
    const matches = byName.get(name);
    if (matches) {
      matches.push(descriptor);
    } else {
      byName.set(name, [descriptor]);
    }
  }

  const result = new Map<Room['id'], string>();
  for (const matches of byName.values()) {
    if (matches.length < 2) {
      continue;
    }

    const ordered = [...matches].sort(creationOrder);
    for (const [index, descriptor] of ordered.entries()) {
      result.set(descriptor.room.id, `#${index + 1}`);
    }
  }

  return result;
};
